/**
 * TURN A SERVER REFUSAL INTO THE SENTENCE THE REVIEWER SHOULD READ.
 *
 * The API refuses with a structured body — a top-level `message`, often a machine-readable `reason`,
 * and an `errors` array naming each offending field. `sendJson` in `../api/client.ts` parses that body
 * into `ApiRequestError.details` and puts only the top-level `message` into the exception's own
 * message, so every screen that renders `error.message` shows the headline and throws the substance
 * away. Two refusals the reviewer actually meets were unusable for exactly that reason:
 *
 *   - Approving with an edit that names a withdrawn field returns five lines explaining which field
 *     and why, and the queue showed only *"Invalid reviewerPatch."*
 *   - Executing a release wave whose approved items name a withdrawn field returns HTTP 409 with
 *     `reason: 'edits_envelope_invalid'`, the work item id and the field — and the release screen
 *     replaced all of it with *"Wave has already been executed or is in progress."*, which is false
 *     about a wave that is still sitting in draft with an Execute button on it.
 *
 * This helper is the one place that decides what a refusal reads as. It never invents structure: a
 * body with nothing usable in it yields the caller's fallback, so a screen can still say something
 * true when the server said nothing.
 */

/** How many field-level errors to spell out before summarising the rest by count. */
const MAX_LISTED_ERRORS = 6;

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

/**
 * One `{field, message}` entry rendered as a line, tolerating either half being absent.
 *
 * `headline` is passed in so a field path the headline already names is not printed a second time. The
 * server's top-level message for a single-error refusal is itself built from the first error, so
 * without this the reviewer reads the same `suggestedPatch._edits[0].locator.field` twice in one
 * sentence — seen in the browser, not in a test, which is why the browser pass is not optional.
 */
function describeEntry(entry: unknown, headline: string | null): string | null {
  const text = nonEmptyString(entry);
  if (text) {
    return text;
  }
  const record = asRecord(entry);
  if (!record) {
    return null;
  }
  const field = nonEmptyString(record.field);
  const message = nonEmptyString(record.message);
  if (field && message) {
    return headline && headline.includes(field) ? message : `${field} — ${message}`;
  }
  return message ?? field;
}

/**
 * Build the fullest true sentence available from a parsed server error body.
 *
 * `fallback` is returned only when the body yields nothing at all — never merged in, because a
 * fallback beside a real explanation reads as a second, competing claim.
 */
export function describeApiErrorBody(details: unknown, fallback: string): string {
  const body = asRecord(details);
  if (!body) {
    return fallback;
  }

  const headline = nonEmptyString(body.message);
  const rawErrors = Array.isArray(body.errors) ? body.errors : [];

  // FILTER FIRST, THEN CAP. Capping first meant six unusable entries followed by the one real
  // explanation showed the reviewer nothing at all — reproduced by the cross-model red-team.
  const usable = rawErrors
    .map((entry) => describeEntry(entry, headline))
    .filter((line): line is string => line !== null);
  const lines = usable.slice(0, MAX_LISTED_ERRORS);

  // The headline of a single-error body usually already embeds that error, so repeating it verbatim
  // would print the same sentence twice.
  const unique = lines.filter((line) => !(headline && headline.includes(line)));

  const parts: string[] = [];
  if (headline) {
    parts.push(headline);
  }
  if (unique.length > 0) {
    parts.push(unique.join(' · '));
  }

  // A COUNT IS NOT AN EXPLANATION. With no headline and no usable entry, the only thing this could
  // have printed was "(1 problems in all; showing 0.)" — a sentence carrying no information, where
  // the caller's fallback at least says what failed. So the count is only ever an ADDITION to real
  // content, never the whole of it.
  if (parts.length === 0) {
    return fallback;
  }

  const total = typeof body.totalErrors === 'number' && Number.isFinite(body.totalErrors)
    ? body.totalErrors
    : rawErrors.length;
  if (total > lines.length) {
    parts.push(`(${total} problems in all; showing ${lines.length}.)`);
  }

  return parts.join(' ');
}

/**
 * The same, for a caught value of unknown shape. Prefers the structured body over the exception's
 * own message, because the body is what carries the per-field detail.
 */
export function describeApiError(reason: unknown, fallback: string): string {
  const withDetails = asRecord(reason) ?? (reason instanceof Error ? (reason as unknown as Record<string, unknown>) : null);
  const exceptionMessage = reason instanceof Error ? nonEmptyString(reason.message) : null;

  // `sendJson` marks the message it invents for a response that explained nothing. That string is
  // this client talking to itself — `Request failed: /api/v1/releases/waves/<id>/execute` — and
  // preferring it over the caller's fallback put an internal endpoint path in front of the reviewer
  // and dropped the sentence telling him what to do next.
  const isGeneric = withDetails?.generic === true;

  if (withDetails && 'details' in withDetails && withDetails.details !== undefined) {
    return describeApiErrorBody(withDetails.details, isGeneric ? fallback : (exceptionMessage ?? fallback));
  }

  return isGeneric ? fallback : (exceptionMessage ?? fallback);
}
